import type { HeroVisualConfig } from '@/lib/hero-visual/types'

// Every editable value of the animated hero visual. Positions and sizes are in
// R (the composition radius), measured from the focus point of the layout.
// Glass shapes are listed back to front and matched to their elements by id.

export const HERO_VISUAL: HeroVisualConfig = {
    seed: 20261103,
    startTime: 40,
    staticTime: 64,
    colors: {
        background: '#0B1E36',
        cyan: '#5EEAFF',
        teal: '#00E5D4',
        ice: '#CFF6FF',
        frost: '#F2FDFF'
    },
    layout: {
        landscape: {
            focusX: 0.665,
            focusY: 0.52,
            radiusOfWidth: 0.3,
            radiusOfHeight: 0.5,
            clearFrom: 0.24,
            clearTo: 0.5,
            networkX: 0.1,
            networkY: 0.02,
            networkWidth: 1.32,
            networkHeight: 0.8
        },
        portrait: {
            focusX: 0.57,
            focusY: 0.68,
            radiusOfWidth: 0.5,
            radiusOfHeight: 0.27,
            clearFrom: 0.3,
            clearTo: 0.54,
            networkX: 0.02,
            networkY: 0.06,
            networkWidth: 1.1,
            networkHeight: 0.95
        }
    },
    parallax: {
        shift: 0.06,
        pointer: 0.85,
        sway: 0.35,
        ease: 2.2
    },
    ambient: [
        { color: 'teal', x: 0.02, y: 0.08, size: 1.5, alpha: 0.11, drift: 0.16, speed: 0.012 },
        { color: 'cyan', x: 0.6, y: -0.5, size: 0.95, alpha: 0.075, drift: 0.2, speed: 0.016 },
        { color: 'ice', x: -0.5, y: 0.62, size: 1.05, alpha: 0.05, drift: 0.22, speed: 0.01 }
    ],
    currents: [
        {
            layer: 'back',
            lines: 8,
            depth: 0.35,
            offsetY: -0.2,
            tilt: -0.1,
            amplitude: 0.32,
            waviness: 0.55,
            flow: 0.032,
            spread: 0.4,
            twist: 0.3,
            thickness: 0.14,
            jitter: 0.018,
            speed: 0.05,
            width: 0.75,
            alpha: 0.36,
            glow: 0.55,
            colors: ['ice', 'cyan'],
            pulses: 1
        },
        {
            layer: 'back',
            lines: 6,
            depth: 0.62,
            offsetY: 0.1,
            tilt: 0.07,
            amplitude: 0.4,
            waviness: 0.7,
            flow: 0.04,
            spread: 0.28,
            twist: 0.4,
            thickness: 0.16,
            jitter: 0.016,
            speed: 0.064,
            width: 1,
            alpha: 0.5,
            glow: 0.8,
            colors: ['cyan', 'teal'],
            pulses: 1
        },
        {
            layer: 'front',
            lines: 3,
            depth: 1.05,
            offsetY: 0.44,
            tilt: -0.05,
            amplitude: 0.34,
            waviness: 0.6,
            flow: 0.05,
            spread: 0.1,
            twist: 0.24,
            thickness: 0.2,
            jitter: 0.012,
            speed: 0.078,
            width: 1.25,
            alpha: 0.64,
            glow: 1,
            colors: ['teal', 'cyan'],
            pulses: 1
        }
    ],
    network: {
        count: { high: 38, medium: 30, low: 22 },
        linkDistance: 0.36,
        speed: 0.03,
        flowScale: 0.85,
        flowSpeed: 0.03,
        linkAlpha: 0.5,
        hubs: 2,
        signals: 2
    },
    ring: {
        radius: 1.42,
        ratio: 0.2,
        ratioDrift: 0.045,
        tilt: -10,
        tiltDrift: 5,
        speed: 0.022,
        alpha: 0.42,
        width: 1,
        satelliteSpeed: 0.16,
        satelliteDrift: 0.06
    },
    glass: [
        {
            id: 'tile',
            kind: 'tile',
            x: 0.6,
            y: -0.46,
            width: 0.4,
            height: 0.4,
            radius: 0.28,
            depth: 0.32,
            blur: 13,
            rotation: 15,
            sway: 8,
            drift: 0.04,
            speed: 0.021,
            refraction: 1.03,
            shift: 0.012,
            smallScreens: true
        },
        {
            id: 'orb',
            kind: 'orb',
            x: 0.02,
            y: -0.06,
            width: 0.92,
            height: 0.92,
            radius: 0.5,
            depth: 0.55,
            blur: 5,
            rotation: 0,
            sway: 0,
            drift: 0.045,
            speed: 0.028,
            refraction: 1.1,
            shift: 0,
            smallScreens: true
        },
        {
            id: 'blade',
            kind: 'blade',
            x: -0.38,
            y: 0.33,
            width: 0.24,
            height: 0.82,
            radius: 0.5,
            depth: 0.85,
            blur: 9,
            rotation: -34,
            sway: 6,
            drift: 0.05,
            speed: 0.024,
            refraction: 1.04,
            shift: 0.014,
            smallScreens: true
        },
        {
            id: 'bead',
            kind: 'bead',
            x: 0.76,
            y: 0.5,
            width: 0.15,
            height: 0.15,
            radius: 0.5,
            depth: 1.15,
            blur: 2,
            rotation: 0,
            sway: 0,
            drift: 0.07,
            speed: 0.037,
            refraction: 1.22,
            shift: 0,
            smallScreens: false
        }
    ],
    bokeh: [
        { color: 'cyan', x: 1.02, y: -0.28, size: 0.12, alpha: 0.08, depth: 1.5, drift: 0.12, speed: 0.018 },
        { color: 'ice', x: -0.2, y: 0.74, size: 0.085, alpha: 0.07, depth: 1.7, drift: 0.1, speed: 0.022 },
        { color: 'teal', x: 0.36, y: -0.8, size: 0.06, alpha: 0.085, depth: 1.35, drift: 0.09, speed: 0.027 }
    ],
    quality: {
        mediumBelow: 1100,
        lowBelow: 700,
        maxPixelRatio: { high: 1.75, medium: 2, low: 2 },
        pixelBudget: { high: 4200000, medium: 3200000, low: 2000000 }
    }
}
